import * as React from 'react';
import { FormControl, Grid, Button, FormLabel } from '@mui/material';
import { markedAsCryopreserveLSM, addCryoPData, printCryoPData, editCryoPData } from "../../../../../bmr/slices/manufacturer";
import { selectStorageLocationDropdown, fetchStorageLocationDropDown } from "../../../../../bmr/slices/storageLocation";
import { useDispatch, useSelector } from "react-redux";
import { cryoInitialsValue } from "../../../../../utils/formik/initialValues"
import { cryoValidationSchema } from "../../../../../utils/formik/validationSchema"
import { useFormik } from "formik";
import FormikArrayPreScreen from "../../../../../pages/appointment-details/common-pre-screen/FormikArrayPreScreen"
import FormikControl from "../../../../../bmr/components/common/FormikControl";
import { selectCurrentCycle } from "../../../../../bmr/slices/common";
import { notify } from '../../../../utils/methods/notification';
import { selectDynamicBags, volumnBagList } from "../../../../../bmr/slices/manufacturer";
import Big from 'big.js';

const safeBig = (val) => {
  try {
    const clean = String(val ?? '').trim();
    if (!clean || clean === 'NaN' || clean === 'undefined' || clean === 'null') return new Big(0);
    return new Big(clean);
  } catch {
    return new Big(0);
  }
};

const CryoLsmFormDialog = ({ showLsm = true, formProps, setOpen, getTimeLine, onReprint }) => {
  const dispatch = useDispatch()
  const storageLocationDropdown = useSelector(selectStorageLocationDropdown)
  const currentCycle = useSelector(selectCurrentCycle)
  const bagDynamic = useSelector(selectDynamicBags)
  const [disabledAll, setDisabledAll] = React.useState(false)
  const [showBtn, setShowBtn] = React.useState(true)

  // 👇 Track which bag row indexes are in edit mode
  const [editingIndexes, setEditingIndexes] = React.useState({})

  React.useEffect(() => {
    const total_blood = (bagDynamic || []).reduce(
      (sum, bag) => sum.plus(safeBig(bag?.cd3_count)),
      new Big(0)
    );
    const remaining_blood = safeBig(currentCycle?.remaining_blood);
    const shouldShow =
      (!remaining_blood.eq(0) || total_blood.lt(remaining_blood)) &&
      currentCycle?.status != 2 &&
      currentCycle?.status != '0';
    setShowBtn(shouldShow);
  }, [bagDynamic, currentCycle?.remaining_blood, currentCycle?.status]);

  React.useEffect(() => {
    dispatch(fetchStorageLocationDropDown())
  }, [])

  React.useEffect(() => {
    if (bagDynamic?.length > 0) setDisabledAll(true)
  }, [bagDynamic])

  // 👇 Toggle edit mode on/off for a specific row index
  const handleEditToggle = (index) => {
    setEditingIndexes(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  // 👇 Save edited row — calls separate edit API
  const handleSaveEdit = async (index) => {
    const errors = await formik.validateForm();
    const rowErrors = errors.blood_bags?.[index];
    if (rowErrors && Object.keys(rowErrors).length > 0) {
      const firstError = Object.values(rowErrors)[0];
      notify.error(firstError);
      return;
    }
    const bag = formik.values.blood_bags?.[index]
    const dynamicBag = bagDynamic?.[index]

    if (!dynamicBag?.id) {
      notify.error('Unable to find bag ID for editing.')
      return
    }

    try {
      // 👇 id in URL, rest in body — matches your curl exactly
      const res = await dispatch(editCryoPData({
        id: dynamicBag.id,                                           // → /bags/update/205
        data: {
          cd3_count: bag?.cd3_count,                               // body
          storage_location_id: bag?.storage_location_id,           // body
          feasibility_assessment_id: currentCycle?.feasibility_assessments_id, // body
        }
      })).unwrap()

      if (res?.status === 200) {
        setEditingIndexes(prev => ({ ...prev, [index]: false }))
        await dispatch(volumnBagList(currentCycle?.feasibility_assessments_id)).unwrap()
        getTimeLine(formProps?.subject_id)
      }
    } catch (err) {
      console.error('Error editing bag:', err)
    }
  }

  const handleCryopreserved = async (values) => {
    try {
      const totalVolume = (values?.blood_bags || []).reduce(
        (sum, bag) => sum.plus(safeBig(bag?.cd3_count)),
        new Big(0)
      );
      const remaining = safeBig(currentCycle?.remaining_blood);
      const monocyte = values?.is_monocyte_added
        ? safeBig(values?.monocyte_depleted_cells)
        : new Big(0);
      const expectedTotal = remaining.plus(monocyte);

      if (totalVolume.gt(expectedTotal)) {
        notify.error(`Total CD3+ count cannot exceed ${expectedTotal.toFixed()}`);
        return false;
      }
      if (totalVolume.lt(expectedTotal)) {
        notify.error(`Total CD3+ count must be exactly ${expectedTotal.toFixed()}`);
        return false;
      }

      delete values?.total_lsm
      const payload = {
        data: values,
        subject_id: formProps?.subject_id,
        inbound_patient_id: formProps?.inbound_patient_id,
      }
      const res = await dispatch(markedAsCryopreserveLSM(payload)).unwrap();
      if (res?.status === 200) {
        setOpen?.(false)
        getTimeLine(formProps?.subject_id)
      }
    } catch (err) {
      console.error("Error LSM Approval:", err);
    }
  }

  const handleCryoLsmForm = async (values) => {
    try {
      let finalValue = values?.blood_bags;
      if (bagDynamic && bagDynamic?.length > 0) {
        finalValue = finalValue?.filter(
          item => !bagDynamic.some(
            dyn => String(dyn.identifier) === String(item.identifier)
          )
        );
      }

      const totalVolume = (finalValue || []).reduce(
        (sum, bag) => sum.plus(safeBig(bag?.cd3_count)),
        new Big(0)
      );

      const remainingBlood = safeBig(currentCycle?.remaining_blood);

      if (totalVolume.gt(remainingBlood)) {
        notify.error('Total volume of all bags must be less than total blood')
        return false
      }

      const payload = {
        ...values,
        blood_bags: finalValue,
        feasibility_assessment_id: currentCycle?.feasibility_assessments_id,
        product_manufacturing_id: currentCycle?.product_manufacturing_id,
      }

      const res = await dispatch(addCryoPData({ data: payload })).unwrap();
      if (res?.status === 200) {
        getTimeLine(formProps?.subject_id)
      }
    } catch (err) {
      console.error("Error LSM Approval:", err);
    }
  }

  const initialValues = React.useMemo(() => ({
    ...cryoInitialsValue,
    total_lsm: currentCycle?.remaining_blood ?? "",
    monocyte_depleted_cells: "",
    feasibility_assessment_id: currentCycle?.feasibility_assessments_id,
    cryopreserve_lsm: true,
    total_blood: currentCycle?.total_blood ?? "",
    blood_bags: Array.isArray(bagDynamic) && bagDynamic.length > 0
      ? bagDynamic.map((bag) => ({
        identifier: bag?.identifier,
        volume: bag.volume ?? "",
        cd3_count: bag.cd3_count ?? "",
        print_count: bag.print_count ?? 0,
        storage_location_id: bag?.storage_location?.id ?? "",
      }))
      : cryoInitialsValue.blood_bags,
  }), [currentCycle?.remaining_blood, bagDynamic]);

  const formik = useFormik({
    initialValues,
    validateOnBlur: false,
    validationSchema: cryoValidationSchema,
    enableReinitialize: true,
    onSubmit: !showLsm ? handleCryoLsmForm : handleCryopreserved,
  });

  // 👇 Per-field disable logic — aware of edit mode per row
  const isBagFieldDisabled = (index, fieldName) => {
    // If this row is in edit mode, always enable cd3_count and storage_location_id
    if (editingIndexes[index]) return false

    // Not in edit mode — fall back to original disable logic
    if (!disabledAll) return false

    const dynamicBag = bagDynamic?.[index]
    const formBag = formik?.values?.blood_bags?.[index]
    if (!dynamicBag) return false

    if (fieldName === 'cd3_count') {
      return (
        String(dynamicBag?.identifier) === String(formBag?.identifier) &&
        safeBig(dynamicBag?.cd3_count).eq(safeBig(formBag?.cd3_count))
      )
    }
    if (fieldName === 'storage_location_id') {
      return (
        String(dynamicBag?.identifier) === String(formBag?.identifier) &&
        Number(dynamicBag?.storage_location?.id) === Number(formBag?.storage_location_id)
      )
    }
    return false
  }

  const formOptions = React.useMemo(() => ([
    ...showLsm ? [
      {
        label: "Total CD3+ Count For Cryopreserve",
        type: "number",
        name: "total_lsm",
        placeholder: "Enter CD3+ Count",
        disabled: true,
        extraContent: "x10⁶ Cells",
        required: true,
        col: 1
      },
      // {
      //   label: "Added Monocyte depleted cells",
      //   type: "checkbox",
      //   name: "is_monocyte_added",
      // },
      // {
      //   label: "Added Monocyte depleted cells",
      //   type: "number",
      //   name: "monocyte_depleted_cells",
      //   placeholder: "Enter Added Monocyte depleted cells",
      //   extraContent: "x10⁶ Cells",
      //   showWhen: (values) => values?.is_monocyte_added === true,
      // },
    ] : [],
    {
      label: showLsm ? "Required Cryopreserved LSM" : "",
      type: "text",
      name: "blood_bags",
      required: true,
      initialChildrenData: {
        identifier: formik?.values?.blood_bags?.length + 1,
        volume: 100,
        cd3_count: "",
        print_count: 0,
        storage_location_id: ""
      },
      multipleField: true,
      col: 4,
      object: true,
      shouldItemDel: showBtn && formik?.values?.blood_bags && formik?.values?.blood_bags?.length > 1,
      extraContent: "ml",
      children: (index) => [
        {
          label: "No. Of Bag",
          type: "number",
          name: `blood_bags.${index}.identifier`,
          placeholder: "Enter number of bags",
          disabled: true,
          extraNote: bagDynamic?.[index]?.status == 1
            ? 'Bag utilized for ' + bagDynamic?.[index]?.cycle_no
            : '',
          noQuery: true,
          required: true,
        },
        {
          label: "CD3+ In Each Bag",
          type: "number",
          name: `blood_bags.${index}.cd3_count`,
          placeholder: "Enter CD3+",
          required: true,
          noQuery: true,
          extraContent: "x10⁶ Cells",
          // 👇 Uses edit-aware disable helper
          disabled: isBagFieldDisabled(index, 'cd3_count'),
        },
        {
          label: "Storage Location",
          type: "select",
          options: storageLocationDropdown,
          name: `blood_bags.${index}.storage_location_id`,
          placeholder: "Enter Storage Location",
          required: true,
          noQuery: true,
          // 👇 Uses edit-aware disable helper
          disabled: isBagFieldDisabled(index, 'storage_location_id'),
        },
        {
          label: "Print Count",
          type: "number",
          name: `blood_bags.${index}.print_count`,
          placeholder: "Print Count",
          noQuery: true,
          disabled: true,
        },
      ],
    }
  ]), [
    showLsm,
    bagDynamic,
    disabledAll,
    storageLocationDropdown,
    formik?.values?.blood_bags,
    editingIndexes,
    showBtn,
  ]);

  const updatePrintCount = async (id, index) => {
    try {
      const payload = { id }
      const res = await dispatch(printCryoPData(payload)).unwrap();
      if (res?.status === 200) {
        await dispatch(volumnBagList(currentCycle?.feasibility_assessments_id)).unwrap();
        onReprint(index)
      }
    } catch (err) {
      notify.error('Unable to Print, Please try in sometime.')
    }
  }

  const handleRprint = (index) => {
    updatePrintCount(bagDynamic?.[index]?.id, index)
  }

  return (
    <div className="w-full flex flex-col">
      <form className="p-3" onSubmit={formik.handleSubmit}>
        {formOptions?.map((props, key) => {
          if (props.showWhen && !props.showWhen(formik.values)) {
            return null;
          }
          return (
            <Grid
              key={'_manual_' + key}
              item
              xs={props.col && !props?.object ? 12 / props.col : 12}
              className={`pt-2 ${props?.object ? 'flex' : ''}`}
            >
              {!props?.object ? (
                <FormControl
                  key={'_control_' + key}
                  variant={props.variant ?? "standard"}
                  fullWidth={props.fullWidth ?? true}
                >
                  {props.label && props.children && (
                    <FormLabel shrink required={props.required} className="text-[black]">
                      {props.label}
                    </FormLabel>
                  )}
                  <div className="relative" key={key}>
                    <FormikControl
                      formik={formik}
                      {...props}
                      className="mt-2"
                    />
                  </div>
                </FormControl>
              ) : (
                <FormControl
                  variant={props.variant ?? "standard"}
                  fullWidth={props.fullWidth ?? true}
                  className={`pr-3 ${props.section ? "pt-3" : ""} ${props.className ?? ""}`}
                >
                  {props.label && (
                    <FormLabel shrink required={props.required} className="text-[black]">
                      {props.label}
                    </FormLabel>
                  )}
                  <FormikArrayPreScreen
                    props={props}
                    bagDynamic={bagDynamic}
                    formik={formik}
                    specialCaseAdd={showBtn}
                    disabledAll={disabledAll}
                    buttonPermission={disabledAll}
                    showDeleteButton={!disabledAll}
                    showPrintButton={bagDynamic?.length > 0}
                    onReprint={handleRprint}
                    // 👇 Edit props passed to child
                    canEditBag={(index) => bagDynamic?.[index]?.status != 1}
                    editingIndexes={editingIndexes}
                    onEditToggle={handleEditToggle}
                    onSaveEdit={handleSaveEdit}
                  />
                </FormControl>
              )}
            </Grid>
          );
        })}
        {showBtn && (
          <Button type="submit" variant="contained" className="w-[200px] h-[40px]">
            Submit
          </Button>
        )}
      </form>
    </div>
  );
}

export default CryoLsmFormDialog;